import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { electronModule } from "@/modules/electron";
import type { MediaControllerInitializeOptions } from "@/store/audio/recording/audio-recording.types";
import type { DesktopAudioRecordingStrategy } from "@/modules/electron/types";
import {
  DesktopRecordingStartFailurePhase,
  DesktopRecordingStartFailureSource,
} from "@/modules/electron/types";
import {
  buildDesktopRecordingRestartRequiredLogInfo,
  DesktopRecordingPreparationState,
  type DesktopControllerKind,
  DesktopRecordingRestartRequiredError,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";

type DesktopCapturePreparationProgress = {
  windowIdResolved: boolean;
  uploadTokenResolved: boolean;
};

function derivePreparationState({
  uploadTokenResolved,
  windowIdResolved,
}: DesktopCapturePreparationProgress): DesktopRecordingPreparationState {
  if (!windowIdResolved) {
    return DesktopRecordingPreparationState.WaitingForWindowId;
  }

  if (!uploadTokenResolved) {
    return DesktopRecordingPreparationState.WaitingForUploadToken;
  }

  return DesktopRecordingPreparationState.PreparedWaitingForStart;
}

export async function createDesktopRecordingInitializationTimeoutError({
  controllerKind,
  logMessage,
  options,
  preparationProgress,
  recordingStrategy,
  timeoutMs,
}: {
  controllerKind: DesktopControllerKind;
  logMessage: string;
  options: MediaControllerInitializeOptions;
  preparationProgress: DesktopCapturePreparationProgress;
  recordingStrategy: DesktopAudioRecordingStrategy;
  timeoutMs: number;
}): Promise<DesktopRecordingRestartRequiredError> {
  const [sdkStateResult, permissionResult] = await Promise.allSettled([
    electronModule.recall.getSdkState(),
    electronModule.recall.getPermissionStatus(),
  ]);
  const recallSdkState = sdkStateResult.status === "fulfilled" ? sdkStateResult.value.state : null;
  const recallSdkErrorMessage =
    sdkStateResult.status === "fulfilled" ? (sdkStateResult.value.errorMessage ?? null) : null;
  const recallPermissions = permissionResult.status === "fulfilled" ? permissionResult.value : null;
  const preparationState = derivePreparationState(preparationProgress);
  const restartRequiredError = new DesktopRecordingRestartRequiredError(
    `initialization timed out after ${timeoutMs}ms`,
    {
      controllerKind,
      failureSource: DesktopRecordingStartFailureSource.RendererInitializationTimeout,
      failurePhase: DesktopRecordingStartFailurePhase.InitializeAndStart,
      startAttemptTrace: options.startAttemptTrace ?? null,
      timeoutMs,
      noteId: options.noteId ?? null,
      meetingEventWindowId: options.meetingEventWindowId ?? null,
      recordingStrategy,
      preparationState,
      windowIdResolved: preparationProgress.windowIdResolved,
      uploadTokenResolved: preparationProgress.uploadTokenResolved,
      recallSdkState,
      recallSdkErrorMessage,
      recallPermissions,
    }
  );

  logger.warn({
    message: logMessage,
    info: objectModule.safeAsJson({
      audioRecordingId: options.audioRecordingId,
      noteId: options.noteId,
      ...buildDesktopRecordingRestartRequiredLogInfo({
        error: restartRequiredError,
      }),
    }),
  });

  return restartRequiredError;
}
